import {type UseQueryOptions, useQuery} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type {z} from 'zod';
import {
  type AppError,
  InvalidTokenError,
  type Result,
} from '../../../../../packages/utils/src';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  GetSkillCheckQuestionsSchema,
  GetSkillCheckResultSchema,
  GetSkillCheckTopSchema,
  PostSkillCheckAnswersResponseSchema,
  type SkillCheckQuestionAnswerType,
} from '../entities/skillCheck/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

type GetSkillCheckTop = z.infer<typeof GetSkillCheckTopSchema>;
export type ResultGetSkillCheckTop = Result<GetSkillCheckTop, AppError>;

type GetSkillCheckTopParams = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export const getSkillCheckTop = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetSkillCheckTopParams): Promise<ResultGetSkillCheckTop> => {
  const request = async (
    accessToken?: string,
  ): Promise<ResultGetSkillCheckTop> => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }

    const path = createPathWithQuery(`/${version}/skill-check/top`, {});
    return defaultClient.get(path, GetSkillCheckTopSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetSkillCheckTopOptions = {
  config?: UseQueryOptions<ResultGetSkillCheckTop>;
  version?: string;
};

export const useGetSkillCheckTop = ({
  version = DEFAULT_API_VERSION,
  config,
}: UseGetSkillCheckTopOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultGetSkillCheckTop>({
    ...config,
    queryKey: ['getSkillCheckTop'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getSkillCheckTop({version, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

type GetSkillCheckQuestions = z.infer<typeof GetSkillCheckQuestionsSchema>;
export type ResultGetSkillCheckQuestions = Result<
  GetSkillCheckQuestions,
  AppError
>;

type GetSkillCheckQuestionsParams = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  categoryId: string;
};

export const getSkillCheckQuestions = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  categoryId,
}: GetSkillCheckQuestionsParams): Promise<ResultGetSkillCheckQuestions> => {
  const request = async (
    accessToken?: string,
  ): Promise<ResultGetSkillCheckQuestions> => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }

    const path = createPathWithQuery(
      `/${version}/skill-check/questions/${categoryId}`,
      {},
    );
    return defaultClient.get(path, GetSkillCheckQuestionsSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });
  return result;
};

type UseGetSkillCheckQuestionsOptions = {
  config?: UseQueryOptions<ResultGetSkillCheckQuestions>;
  version?: string;
  categoryId: string;
};

export const useGetSkillCheckQuestions = ({
  version = DEFAULT_API_VERSION,
  categoryId,
  config,
}: UseGetSkillCheckQuestionsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultGetSkillCheckQuestions>({
    ...config,
    queryKey: ['getSkillCheckQuestions', categoryId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getSkillCheckQuestions({
        version,
        cookies,
        categoryId,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

type PostSkillCheckAnswersArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  categoryId: number;
  answers: SkillCheckQuestionAnswerType[];
};

export const postSkillCheckAnswers = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  categoryId,
  answers,
}: PostSkillCheckAnswersArgs) => {
  const request = async (accessToken?: string) => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }

    return defaultClient.post(
      `/${version}/skill-check/answers/${categoryId}`,
      PostSkillCheckAnswersResponseSchema,
      {answers},
    );
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type GetSkillCheckResult = z.infer<typeof GetSkillCheckResultSchema>;
export type ResultGetSkillCheckResult = Result<GetSkillCheckResult, AppError>;

type GetSkillCheckResultParams = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  uuid: string;
};

export const getSkillCheckResult = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  uuid,
}: GetSkillCheckResultParams): Promise<ResultGetSkillCheckResult> => {
  const request = async (
    accessToken?: string,
  ): Promise<ResultGetSkillCheckResult> => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }

    const path = createPathWithQuery(
      `/${version}/skill-check/result/${uuid}`,
      {},
    );
    return defaultClient.get(path, GetSkillCheckResultSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetSkillCheckResultOptions = {
  config?: UseQueryOptions<ResultGetSkillCheckResult>;
  version?: string;
  uuid: string;
};

export const useGetSkillCheckResult = ({
  version = DEFAULT_API_VERSION,
  uuid,
  config,
}: UseGetSkillCheckResultOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultGetSkillCheckResult>({
    ...config,
    queryKey: ['getSkillCheckResult', uuid],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getSkillCheckResult({version, uuid, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};
